import { apiClient } from "./apiClient";
import type { ApiResponse, PagedResult } from "./types";
import { unwrapApiResponse, unwrapPagedItems } from "../shared/utils/apiResponse";

export async function postPaged<T>(url: string, body: Record<string, unknown>) {
  const { data } = await apiClient.post<ApiResponse<PagedResult<T>>>(url, body);
  return unwrapPagedItems(data);
}

export async function saveBulk<T>(url: string, rows: T[]) {
  const { data } = await apiClient.post<ApiResponse<number>>(url, rows);
  return data;
}

export async function deleteEntry(url: string, id: string | number) {
  const { data } = await apiClient.delete<ApiResponse<unknown>>(`${url}/${id}`);
  return data;
}

export async function bulkDeleteEntry(url: string, ids: Array<string | number>) {
  const { data } = await apiClient.post<ApiResponse<number>>(`${url}/bulk-delete`, { ids });
  return unwrapApiResponse<number>(data) ?? 0;
}

export async function permanentlyDeleteEntry(url: string, id: string | number) {
  const { data } = await apiClient.delete<ApiResponse<unknown>>(`${url}/${id}/permanent`);
  return data;
}

export async function getEntry<T>(url: string, id: string | number) {
  const { data } = await apiClient.get<ApiResponse<T>>(`${url}/${id}`);
  return unwrapApiResponse<T>(data);
}

export async function createEntry<T = unknown>(url: string, payload: Record<string, unknown>) {
  const { data } = await apiClient.post<ApiResponse<T>>(url, payload);
  return unwrapApiResponse<T>(data);
}

export async function updateEntry<T = unknown>(url: string, id: string | number, payload: Record<string, unknown>) {
  const { data } = await apiClient.put<ApiResponse<T>>(`${url}/${id}`, payload);
  return unwrapApiResponse<T>(data);
}

export async function getDailyPairDetails<T>(filter: Record<string, unknown>) {
  const { data } = await apiClient.post<ApiResponse<T[]>>("/daily-entries/pair-details", filter);
  return unwrapApiResponse<T[]>(data) ?? [];
}

export async function markDailyGroupPaid(payload: Record<string, unknown>) {
  const { data } = await apiClient.post<ApiResponse<number>>("/daily-entries/mark-group-paid", payload);
  return unwrapApiResponse<number>(data) ?? 0;
}

export async function getDailyPendingReport<T>(filter: Record<string, unknown>) {
  const { data } = await apiClient.get<ApiResponse<T[]>>("/daily-entries/report/pending", { params: filter });
  return unwrapApiResponse<T[]>(data) ?? [];
}

export async function downloadDailyPendingReportExcel(filter: Record<string, unknown>) {
  const { data } = await apiClient.get<Blob>("/daily-entries/report/pending/excel", { params: filter, responseType: "blob" });
  return data;
}

export async function createBillEntry(payload: Record<string, unknown>) {
  const { data } = await apiClient.post<ApiResponse<number>>("/bill-entries", payload);
  return unwrapApiResponse<number>(data);
}

export async function getBillEntry<T>(id: string | number) {
  const { data } = await apiClient.get<ApiResponse<T>>(`/bill-entries/${id}`);
  return unwrapApiResponse<T>(data);
}

export async function getBillEntryPdf(id: string | number, roundToNearestRupee = true) {
  const { data } = await apiClient.get<Blob>(`/bill-entries/${id}/pdf`, {
    params: { roundToNearestRupee },
    responseType: "blob"
  });
  return data;
}

export async function getBulkBillEntryPdf(billIds: number[], roundToNearestRupee = true) {
  const { data } = await apiClient.post<Blob>("/bill-entries/bulk-pdf", { billIds, roundToNearestRupee }, { responseType: "blob" });
  return data;
}

export async function updateBillEntry(id: string | number, payload: Record<string, unknown>) {
  const { data } = await apiClient.put<ApiResponse<unknown>>(`/bill-entries/${id}`, payload);
  return data;
}

export async function deleteBillEntry(id: string | number) {
  const { data } = await apiClient.delete<ApiResponse<unknown>>(`/bill-entries/${id}`);
  return data;
}

export async function bulkDeleteBillEntries(ids: Array<string | number>) {
  const { data } = await apiClient.post<ApiResponse<number>>("/bill-entries/bulk-delete", { ids });
  return unwrapApiResponse<number>(data) ?? 0;
}

export async function permanentlyDeleteBillEntry(id: string | number) {
  const { data } = await apiClient.delete<ApiResponse<unknown>>(`/bill-entries/deleted/${id}/permanent`);
  return data;
}

export async function previewBillEntry<T>(payload: Record<string, unknown>) {
  const { data } = await apiClient.post<ApiResponse<T>>("/bill-entries/preview", payload);
  return unwrapApiResponse<T>(data);
}

export async function getPendingBills<T>(partyId: string | number) {
  const { data } = await apiClient.get<ApiResponse<T[]>>("/bill-entries/pending", { params: { partyId } });
  return unwrapApiResponse<T[]>(data) ?? [];
}

export async function getBillEmailPreview<T>(id: string | number) {
  const { data } = await apiClient.get<ApiResponse<T>>(`/bill-entries/${id}/email-preview`);
  return unwrapApiResponse<T>(data);
}

export async function sendBillEmail(id: string | number, payload: { to: string; subject: string; body: string }) {
  const { data } = await apiClient.post<ApiResponse<string>>(`/bill-entries/${id}/send-email`, payload);
  return data;
}

export type BulkBillEmailPreviewGroup = {
  partyId?: number | null;
  partyName?: string;
  to?: string | null;
  hasEmail?: boolean;
  subject?: string;
  bodyHtml?: string | null;
  billCount?: number;
  billNos?: string[];
  billIds?: number[];
};

export type BulkBillEmailPreview = {
  fromEmail?: string;
  fromName?: string;
  groups: BulkBillEmailPreviewGroup[];
};

export async function getBulkBillEmailPreview(billIds: number[]) {
  const { data } = await apiClient.post<ApiResponse<BulkBillEmailPreview>>("/bill-entries/bulk-email-preview", { billIds });
  return unwrapApiResponse<BulkBillEmailPreview>(data);
}

export async function sendBulkBillEmail(payload: {
  billIds: number[];
  emailOverrides?: { partyId: number; email: string; subject?: string; bodyHtml?: string }[];
}) {
  const { data } = await apiClient.post<ApiResponse<unknown>>("/bill-entries/bulk-email", payload);
  return data;
}

export async function getBillPartyBalance<T>(partyId: string | number) {
  const { data } = await apiClient.get<ApiResponse<T>>("/bill-entries/party-balance", { params: { partyId } });
  return unwrapApiResponse<T>(data);
}

export async function getBillLedgerBalance<T>(ledgerId: string | number) {
  const { data } = await apiClient.get<ApiResponse<T>>("/bill-entries/ledger-balance", { params: { ledgerId } });
  return unwrapApiResponse<T>(data);
}

export async function getBillBankAccountBalance<T>(bankAccountId: string | number) {
  const { data } = await apiClient.get<ApiResponse<T>>("/bill-entries/bank-account-balance", { params: { bankAccountId } });
  return unwrapApiResponse<T>(data);
}
